import { DatePicker, Link, Tooltip } from '@globalmed/atlas-ui';
import cn from 'classnames';
import dayjs from 'dayjs';
import { ReactElement, useEffect } from 'react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styles from './InstitutionContractPeriods.module.css';
import { IntegerInput } from '@/components/IntegerInput';
import {
  EMPTY_INSTITUTION_CONTRACT,
  TInstitutionProvisioningForm,
} from '@pages/AdminPanel/schemas';
import { getDateOnlyISOFormat } from '@/utils/convertDate';
import { hasContractStarted } from '@/utils/organizationContracts';
import { parseNumericInput } from '@/utils/parseNumericInput';

type TSavedContract = {
  id?: string;
  contractStartDate?: string;
  contractEndDate?: string;
};

type TProps = {
  disabled?: boolean;
  showDisclaimer?: boolean;
  occupiedSeats?: number;
  savedContracts?: TSavedContract[];
  isScrollable?: boolean;
};

const CONTRACT_FIELDS = [
  'seats',
  'stationLicenses',
  'contractStartDate',
] as const;

/**
 * An institution's contract periods, each with its seats, station licenses
 * and dates, and a link to add a new one. The end date is not editable: it
 * follows the start date, one year on, minus one day, while a saved contract
 * whose start is left as it was keeps its stored end. A saved contract is
 * locked once it has started. Every period but the first can be deleted
 * until it starts.
 * @param props - Component props.
 * @param props.disabled - When true, every field is locked for editing and no
 * contract can be added or deleted.
 * @param props.showDisclaimer - When true, each field is wrapped in a tooltip
 * saying GlobalMed controls the values.
 * @param props.occupiedSeats - The institution's occupied seats, interpolated
 * into the "cannot be lower than occupied" seats error message.
 * @param props.savedContracts - The contracts as the institution was loaded,
 * used to lock the ones that have started and keep them from being deleted.
 * @param props.isScrollable - When true, the periods scroll within a capped
 * height, keeping the add link in view (for use inside a modal).
 * @returns JSX.Element representing the institution's contract periods.
 */
export const InstitutionContractPeriods = ({
  disabled = false,
  showDisclaimer = false,
  occupiedSeats,
  savedContracts = [],
  isScrollable = false,
}: TProps) => {
  const { t } = useTranslation();
  const {
    control,
    setValue,
    subscribe,
    getValues,
    getFieldState,
    trigger,
    formState: { isSubmitting },
  } = useFormContext<{ provisioning: TInstitutionProvisioningForm }>();
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'provisioning.contracts',
    keyName: 'fieldKey',
  });

  const currentYear = dayjs().year();
  // Whether a contract may start depends on the others, so a change to one
  // re-checks the fields already filled in or flagged on all of them.
  useEffect(
    () =>
      subscribe({
        name: 'provisioning',
        formState: { values: true },
        callback: ({ name }) => {
          if (!name) return;
          const names = getValues('provisioning.contracts').flatMap(
            (contract, index) =>
              CONTRACT_FIELDS.filter(
                (field) =>
                  !!contract[field] ||
                  !!getFieldState(`provisioning.contracts.${index}.${field}`)
                    .error,
              ).map(
                (field) => `provisioning.contracts.${index}.${field}` as const,
              ),
          );
          if (names.length) void trigger(names);
        },
      }),
    [subscribe, getValues, getFieldState, trigger],
  );

  const withDisclaimer = (children: ReactElement) =>
    showDisclaimer ? (
      <Tooltip
        title={t('admin-panel.provisioning-controlled-by-globalmed')}
        direction="top"
        type="white"
        tail
      >
        {children}
      </Tooltip>
    ) : (
      children
    );

  return (
    <div className={styles.contracts}>
      <div className={cn(styles.list, { [styles.scrollable]: isScrollable })}>
        {fields.map((field, index) => {
          const savedContract = savedContracts.find(
            (contract) => !!field.id && contract.id === field.id,
          );
          const isLocked =
            disabled || hasContractStarted(savedContract?.contractStartDate);
          const title = t('create-organization-modal.contract-period', {
            number: index + 1,
          });

          return (
            <section
              key={field.fieldKey}
              className={styles.contract}
              aria-label={title}
            >
              {fields.length > 1 && (
                <div className={styles.header}>
                  <span className={styles.title}>{title}</span>
                  {!isLocked && index > 0 && (
                    <Link
                      preIcon="XClose"
                      color="destructive"
                      size="sm"
                      aria-label={t(
                        'create-organization-modal.delete-contract-period',
                        { number: index + 1 },
                      )}
                      disabled={isSubmitting}
                      onClick={() => remove(index)}
                    />
                  )}
                </div>
              )}
              <Controller
                name={`provisioning.contracts.${index}.seats`}
                control={control}
                render={({ field: seats, fieldState }) =>
                  withDisclaimer(
                    <IntegerInput
                      label={t('create-organization-modal.number-of-seats')}
                      placeholder="200"
                      value={seats.value?.toString() ?? ''}
                      onChange={(value) =>
                        seats.onChange(parseNumericInput(value))
                      }
                      min="1"
                      thousandSeparator
                      disabled={isLocked}
                      isError={!!fieldState.error}
                      message={t(fieldState.error?.message ?? '', {
                        count: occupiedSeats,
                      })}
                      required
                      width="100%"
                    />,
                  )
                }
              />
              <Controller
                name={`provisioning.contracts.${index}.stationLicenses`}
                control={control}
                render={({ field: stationLicenses, fieldState }) =>
                  withDisclaimer(
                    <IntegerInput
                      label={t('create-organization-modal.station-licenses')}
                      placeholder="15"
                      value={stationLicenses.value?.toString() ?? ''}
                      onChange={(value) =>
                        stationLicenses.onChange(parseNumericInput(value))
                      }
                      min="1"
                      disabled={isLocked}
                      isError={!!fieldState.error}
                      message={t(fieldState.error?.message ?? '')}
                      required
                      width="100%"
                    />,
                  )
                }
              />
              <div className={styles.row}>
                <Controller
                  name={`provisioning.contracts.${index}.contractStartDate`}
                  control={control}
                  render={({ field: startDate, fieldState }) =>
                    withDisclaimer(
                      <DatePicker
                        label={t('admin-panel.provisioning-contract-start')}
                        disabled={isLocked}
                        value={
                          startDate.value
                            ? dayjs(startDate.value).toDate()
                            : null
                        }
                        onChange={(date) => {
                          const start = date
                            ? getDateOnlyISOFormat(dayjs(date))
                            : '';
                          startDate.onChange(start);
                          setValue(
                            `provisioning.contracts.${index}.contractEndDate`,
                            savedContract &&
                              start === savedContract.contractStartDate
                              ? (savedContract.contractEndDate ?? '')
                              : start
                                ? getDateOnlyISOFormat(
                                    dayjs(start)
                                      .add(1, 'year')
                                      .subtract(1, 'day'),
                                  )
                                : '',
                            { shouldDirty: true },
                          );
                        }}
                        isError={!!fieldState.error}
                        message={t(fieldState.error?.message ?? '')}
                        yearsRange={[currentYear - 5, currentYear + 10]}
                        required
                        width="100%"
                      />,
                    )
                  }
                />
                <Controller
                  name={`provisioning.contracts.${index}.contractEndDate`}
                  control={control}
                  render={({ field: endDate }) =>
                    withDisclaimer(
                      <DatePicker
                        label={t('admin-panel.provisioning-contract-end')}
                        disabled
                        value={
                          endDate.value ? dayjs(endDate.value).toDate() : null
                        }
                        yearsRange={[currentYear - 5, currentYear + 11]}
                        required
                        width="100%"
                      />,
                    )
                  }
                />
              </div>
            </section>
          );
        })}
      </div>

      {!disabled && (
        <Link
          className={styles.addLink}
          text={t('create-organization-modal.add-contract-period')}
          preIcon="Plus"
          disabled={isSubmitting}
          onClick={() => append(EMPTY_INSTITUTION_CONTRACT)}
        />
      )}
    </div>
  );
};
